import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';

export interface CgOption {
  value: string;
  label: string;
  disabled?: boolean;
  // Optional small icon shown before the label (e.g. a theme logo).
  icon?: string;
}

// A small custom combo-box that matches the themes page filter dropdown: an input
// styled trigger with a border-triangle caret, and a themed options panel on open
// (instead of the OS-native <select> list). Drop-in replacement for a styled
// native select: bind [options] / [value] and listen to (valueChange).
@Component( {
  selector: 'app-cg-select',
  templateUrl: './cg-select.component.html',
  styleUrls: [ './cg-select.component.scss' ],
} )
export class CgSelectComponent {

  @Input() options: CgOption[] = [];
  @Input() value = '';
  @Input() ariaLabel = '';
  // Shown when nothing is selected (value is empty / not in options).
  @Input() placeholder = '';

  @Output() valueChange = new EventEmitter<string>();

  open = false;

  constructor( private el: ElementRef ) {
  }

  get selected(): CgOption | undefined {
    return this.options.find( opt => opt.value === this.value );
  }

  get selectedLabel(): string {
    return this.selected ? this.selected.label : this.placeholder;
  }

  // The list opens upward when there is no room below (a control near the bottom of a phone
  // screen would otherwise drop its options under the mobile tab bar, where taps go to the bar).
  up = false;

  toggle( ev: Event ): void {
    ev.stopPropagation();
    this.open = !this.open;
    if ( this.open && typeof window !== 'undefined' ) {
      const rect = this.el.nativeElement.getBoundingClientRect();
      const need = Math.min( 241, this.options.length * 38 + 8 ) + 80;
      this.up = window.innerHeight - rect.bottom < need && rect.top > need;
    }
  }

  select( opt: CgOption ): void {
    if ( opt.disabled ) {
      return;
    }
    this.open = false;
    if ( opt.value !== this.value ) {
      this.value = opt.value;
      this.valueChange.emit( opt.value );
    }
  }

  // Close when clicking anywhere outside this dropdown.
  @HostListener( 'document:click', [ '$event' ] )
  onDocumentClick( ev: Event ): void {
    if ( this.open && !this.el.nativeElement.contains( ev.target ) ) {
      this.open = false;
    }
  }

  // Close on Escape.
  @HostListener( 'document:keydown.escape' )
  onEscape(): void {
    this.open = false;
  }
}
